import { fileNameFormat } from "../../../common/services/Utilities";
import { KeyValue, Query, SearchPillData } from "../../../interfaces";
import * as React from "react";
import SearchPills from "../../../containers/SearchPills";
import {
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import {
  sodStatesArray,
  stateNameToAbbrv,
} from "../../../refdata/FilterOptions";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import { useHistory, useLocation } from "react-router-dom";
import ShowHideButton from "../../ShowHideButton";

const useStyles = makeStyles(theme => ({
  tableWrapper: {
    overflowX: "auto",
  },
  heading: {
    color: theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 26,
    fontWeight: "bold",
    whiteSpace: "nowrap",
    paddingBottom: "1rem",
  },
  footerText: {
    color: theme.palette.primary.main,
    width: 900,
    minWidth: 900,
    fontSize: 16,
    fontWeight: "bold",
  },
  tableCellDarken: {
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },
  tableHead: {
    border: 0,
    backgroundColor: "#4c77b7",
    color: "#ffffff",
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontWeight: 700,
    fontStyle: "normal",
    lineHeight: "normal",
    textAlign: "center",
  },
  subHeader: {
    backgroundColor: "#194480",
    color: "#ffffff",
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontWeight: 700,
    fontStyle: "normal",
    lineHeight: "normal",
    textAlign: "center",
  },
  tableHeadFirst: {
    border: 0,
    backgroundColor: "#194480",
    color: "#ffffff",
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontWeight: 700,
    fontStyle: "normal",
    lineHeight: "normal",
  },
  subTitle: {
    backgroundColor: "#194480",
    color: "#ffffff",
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontWeight: 700,
    fontStyle: "normal",
    lineHeight: "normal",
    whiteSpace: "nowrap",
  },
  tableBody: {
    "color": "#3485FF",
    "fontFamily": "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    "fontWeight": 700,
    "fontStyle": "normal",
    "lineHeight": "normal",
    "textDecoration": "underline",
    "textAlign": "center",
    "&:hover": { backgroundColor: theme.palette.secondary.main },
    "cursor": "pointer",
  },
  tableBodyAlt: {
    color: theme.palette.primary.main,
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontWeight: 700,
    fontStyle: "normal",
    lineHeight: "normal",
    textAlign: "center",
  },
  tableBodyFirstColumn: {
    color: "#194480",
    fontFamily: "SourceSansPro-Bold, Source Sans Pro Bold, Source Sans Pro",
    fontStyle: "normal",
    lineHeight: "normal",
    backgroundColor: "rgba(0, 0, 0, 0.03)",
  },
  tableBodyEven: {
    backgroundColor: "#e7ecf2",
  },
  tableBodyOdd: {
    backgroundColor: "#fffff",
  },
}));

interface Props {
  appState: Query;
}

export default function SummaryTablesTimeSeries({ appState }: Props) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label: "Start Date",
      value: `06/30/${appState.filters.startDate}`,
      search: false,
      static: true,
      formKey: "startDate",
    },
    {
      label: "End Date",
      value: `06/30/${appState.filters.endDate}`,
      search: false,
      static: true,
      formKey: "endDate",
    },
    {
      label: "",
      value: appState.ui.institutionTypeTimeSeries,
      search: false,
      static: true,
      formKey: "institutionTypeTimeSeries",
    },
    {
      label: "Totals by",
      value: appState.ui.totalsType,
      search: false,
      static: true,
      formKey: "totalsType",
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={summaryTablesSearchPills} />}
    />
  );
  const [previousStartDate, setPreviousStartDate] = React.useState(
    appState.filters.startDate,
  );
  const [previousEndDate, setPreviousEndDate] = React.useState(
    appState.filters.endDate,
  );
  const [
    previousInstitutionTypeTimeSeries,
    setPreviousInstitutionTypeTimeSeries,
  ] = React.useState(appState.ui.institutionTypeTimeSeries);
  const [previousTotalsType, setPreviousTotalsType] = React.useState(
    appState.ui.totalsType,
  );
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [years, setYears] = React.useState(["State"]);
  const [arrays, setArrays] = React.useState<KeyValue>([]);

  const classLookup: KeyValue = React.useMemo(() => {
    return {
      "All Institutions": "all",
      "Commercial Banks": "CB",
      "Savings Institutions": "SA",
      "U.S. Branches of Foreign Banks": "IB",
    };
  }, []);

  React.useEffect(() => {
    if (
      (appState.ui.totalsType !== previousTotalsType ||
        appState.ui.institutionTypeTimeSeries !==
          previousInstitutionTypeTimeSeries ||
        appState.filters.startDate !== previousStartDate ||
        appState.filters.endDate !== previousEndDate ||
        initialLoad) &&
      appState.ui.reportType
    ) {
      setYears(getYears(appState.filters.startDate, appState.filters.endDate));
      initialLoad && setInitialLoad(false);
      setPreviousTotalsType(appState.ui.totalsType);
      setPreviousInstitutionTypeTimeSeries(
        appState.ui.institutionTypeTimeSeries,
      );
      setPreviousStartDate(appState.filters.startDate);
      setPreviousEndDate(appState.filters.endDate);
      let isMounted = true;
      const getUnique =
        appState.ui.totalsType === "Institutions" ? "&get_unique=CERT" : "";
      (async () => {
        await Promise.all(
          getYears(appState.filters.startDate, appState.filters.endDate).map(
            year => {
              return fetchData(
                `${ApiBaseUrl()}/sod?agg_by=STALPBR${getUnique}&agg_sum_fields=DEPSUMBR,BKMO&filters=YEAR:${year}${
                  appState.ui.institutionTypeTimeSeries !== "All Institutions"
                    ? ` AND INSURED:${
                        classLookup[appState.ui.institutionTypeTimeSeries]
                      }`
                    : ""
                }`,
              );
            },
          ),
        )
          .then(response => {
            const arrays: KeyValue[] = [];
            response.forEach(item => {
              const totalsLookup: KeyValue = {
                Institutions: item.totals?.sum_BKMO,
                Deposits: item.totals?.sum_DEPSUMBR,
              };
              arrays.push([
                ...item.data,
                {
                  totals: totalsLookup[appState.ui.totalsType]
                    ? totalsLookup[appState.ui.totalsType]
                    : item.totals?.count,
                },
              ]);
            });
            if (isMounted) {
              setArrays(arrays);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
          });
      })();
    }
  }, [
    appState.filters,
    initialLoad,
    appState.ui,
    years,
    previousStartDate,
    previousEndDate,
    previousTotalsType,
    previousInstitutionTypeTimeSeries,
    classLookup,
  ]);

  const getYears = (startYear: number, endYear: number): string[] => {
    const years: string[] = [];

    for (let year = startYear; year <= endYear; year++) {
      years.push(year.toString());
    }
    return years;
  };

  const tableHeader = ["State", ...years];

  const tableData: KeyValue[] = [];

  tableData.push(["State", ...years]);

  [...sodStatesArray, "Totals**"].forEach(state => {
    const row = [state];
    years.forEach(year => {
      const matchingState =
        arrays[years.indexOf(year)] &&
        arrays[years.indexOf(year)].find(
          (item: KeyValue) => item.STALPBR === stateNameToAbbrv[state],
        );
      const depSumFraction = (value: number) =>
        appState.ui.totalsType === "Deposits"
          ? (value / 1000).toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })
          : value.toLocaleString();
      let rowValue = "0";
      if (matchingState && appState.ui.totalsType === "Institutions") {
        rowValue = matchingState.unique_count;
      }
      if (matchingState && appState.ui.totalsType === "Branch Offices") {
        rowValue = matchingState.count;
      }
      if (matchingState && appState.ui.totalsType === "Deposits") {
        rowValue = (matchingState.sum_DEPSUMBR / 1000)?.toLocaleString(
          undefined,
          {
            maximumFractionDigits: 0,
          },
        );
      }
      if (state === "Totals**") {
        rowValue = depSumFraction(
          arrays[years.indexOf(year)]?.find((sum: KeyValue) => sum.totals)
            ?.totals || 0,
        );
      }
      row.push(rowValue);
    });

    tableData.push(row);
  });

  type Props = {
    tableData: KeyValue[];
  };

  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat(
        `Summary_Tables_Time_Series_${appState.ui.institutionTypeTimeSeries}_${appState.ui.totalsType}`,
      )}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
    },
  ];

  const headingTitle = (
    <>
      <Typography className={classes.heading}>
        Time Series by States & Territories
      </Typography>
      <div style={{ position: "relative", right: 0, top: 5 }}>
        <ApiDataDownload app="SOD" apiData={apiData} downloadData={tableData} />
      </div>
    </>
  );

  const tableCellProps = (
    instType: string,
    year: string,
    rowData: any,
    noOnClick: boolean,
  ) => {
    const props = {
      onClick: noOnClick
        ? undefined
        : (e: any) => {
            appState.filters.stalpbr = stateNameToAbbrv[rowData[0]];
            appState.ui.reportType = "nationalTimeSeries";
            appState.filters.lastYear = year;
            appState.ui.summaryTableType = "nationalTimeSeries";
            if (e.key === "Enter" || e.nativeEvent.type === "click") {
              history.push({
                state: {
                  appState: appState,
                  backResults: `${location.pathname}${location.search}`,
                },
                pahtname: "/SOD/summaryTables",
                search: `?summaryTableLevelTwo=true&instType=${instType}&reportType=nationalTimeSeries`,
              });
            }
          },
      tabIndex: 0,
    };
    return props;
  };
  const TimeSeriesTable: React.FC<Props> = ({ tableData }) => {
    const tableDataBody = [...tableData];
    tableDataBody.pop();
    const instType = classLookup[appState.ui.institutionTypeTimeSeries];
    return (
      <div className={classes.tableWrapper}>
        <Table>
          <TableHead>
            <TableRow>
              {tableHeader.map((header: string, index: number) => (
                <TableCell
                  key={index}
                  className={
                    index === 0 ? classes.tableHeadFirst : classes.tableHead
                  }
                >
                  {header === "1994" ? "1994*" : header}
                </TableCell>
              ))}
            </TableRow>
            {appState.ui.totalsType === "Deposits" && (
              <TableRow>
                {tableHeader.map((header: string, index: number) => (
                  <TableCell className={classes.subTitle} key={index}>
                    {index === 0 ? "Deposit Amounts In Millions USD" : ""}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {tableDataBody.slice(1).map((row, index) => (
              <TableRow
                className={
                  index % 2 === 1 ? classes.tableBodyEven : classes.tableBodyOdd
                }
                key={index}
              >
                {row.map((cell: string, index: number) => (
                  <TableCell
                    {...tableCellProps(
                      instType,
                      years[index - 1],
                      row,
                      cell === "0" || index === 0,
                    )}
                    className={
                      index === 0
                        ? classes.tableBodyFirstColumn
                        : cell === "0" || index === 0
                        ? classes.tableBodyAlt
                        : classes.tableBody
                    }
                    key={index}
                  >
                    {cell ? cell.toLocaleString() : 0}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            <TableRow>
              {tableData[tableData.length - 1].map(
                (element: string, index: number) => (
                  <TableCell
                    className={
                      index === 0 ? classes.tableHeadFirst : classes.subHeader
                    }
                    key={index}
                  >
                    {element}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  };
  return (
    <>
      {headingTitle}
      <Divider />
      {searchPills}
      <TimeSeriesTable tableData={tableData} />
      {appState.filters.startDate === "1994" && (
        <Typography className={classes.footerText}>
          *Includes 18 conservatorships
        </Typography>
      )}
      <Typography className={classes.footerText}>
        **Institution amounts represent the number of unique institutions that
        maintain a deposit taking office in each state as of June 30.
      </Typography>
    </>
  );
}
